import { VulnerabilitiesApiGetEolDashboardDetailsRequest } from '@ariksa/inventory-core';
import {
  ContextApiGetRiskContextResRequest,
  CountSummary,
  EOLDashboard,
  InsightV2Pagination,
  ResourceSummaryApiGetSummaryByTypeRequest,
  RiskContextResponse,
  VulnerabilitiesApiGetEolEntitiesDashboardDetailsRequest,
} from '@ariksa/inventory-core/api';
import { ChartsApiGetVulnerabilitiesDetailsRequest } from '@ariksa/reporting/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryAction } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';
import {
  DefaultApiGetBySourcesDashboardDataRequest,
  DefaultApiGetCriticalAndHighSeveritySummaryRequest,
  DefaultApiGetHighestOccurringVulnerabilitiesRequest,
  DefaultApiGetMostWidelyDeployedLibVulnerabilitiesRequest,
  DefaultApiGetTopNewVulnerabilitiesRequest,
  DefaultApiGetVulnerabilityCountByAgnosticResourceRequest,
  DefaultApiGetVulnerabilityPriortizationDataRequest,
} from 'services/Vulnerability/legacyRequestTypes';
import {
  VulnerabilityDashboardBySources,
  VulnerabilityDashboardCount,
  VulnerabilityDashboardCriticalHigh,
  VulnerabilityDashboardHighestItem,
  VulnerabilityDashboardLibraryItem,
  VulnerabilityDashboardTopItem,
  VulnerabilityPrioritization,
} from 'services/Vulnerability/types';

import { PageInfo } from 'components/Navigation';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState } from './types';

// The initial state of the Tag container
export const initialState: ContainerState = {
  //vulnerabilities
  topNewVulnerabilities: QueryState.init([]),
  highestOccurringVulnerabilities: QueryState.init([]),
  mostWidelyDeployedLibraries: QueryState.init([]),
  vulnerableEntitiesWithSensitiveDataAccess: QueryState.init({}),
  criticalAndHighVulnerabilitiesSummary: QueryState.init({}),
  eolSoftware: QueryState.init({} as EOLDashboard),
  vulnerabilitiesCount: {
    vm: QueryState.init({} as VulnerabilityDashboardCount),
    vmImage: QueryState.init({} as VulnerabilityDashboardCount),
    containerService: QueryState.init({} as VulnerabilityDashboardCount),
    container: QueryState.init({} as VulnerabilityDashboardCount),
    serverless: QueryState.init({} as VulnerabilityDashboardCount),
    totalVulnerabilities: QueryState.init({} as VulnerabilityDashboardCount),
  },
  allEolSoftware: PagedQueryState.init([]),
  eolSoftwareEntities: PagedQueryState.init([]),
  riskContext: PagedQueryState.init({}),
  vulnerabilityPrioritization: QueryState.init(
    {} as VulnerabilityPrioritization,
  ),
  vulnerabilitiesByResourceTypes: QueryState.init(
    {} as VulnerabilityDashboardBySources,
  ),
  exploitableEntitiesToPatch: QueryState.init({}),
};

const vulnerabilityOverviewSlice = createSlice({
  name: 'vulnerabilityOverview',
  initialState,
  reducers: {
    /*--------------------------Vulnerability overview------------------------*/
    getTopNewVulnerabilities(
      state,
      action: QueryAction<
        VulnerabilityDashboardTopItem[],
        DefaultApiGetTopNewVulnerabilitiesRequest
      >,
    ) {
      state.topNewVulnerabilities = QueryState.next(
        state.topNewVulnerabilities,
        action,
      );
    },

    getHighestOccurringVulnerabilities(
      state,
      action: QueryAction<
        VulnerabilityDashboardHighestItem[],
        DefaultApiGetHighestOccurringVulnerabilitiesRequest
      >,
    ) {
      state.highestOccurringVulnerabilities = QueryState.next(
        state.highestOccurringVulnerabilities,
        action,
      );
    },

    getMostWidelyDeployedLibraries(
      state,
      action: QueryAction<
        VulnerabilityDashboardLibraryItem[],
        DefaultApiGetMostWidelyDeployedLibVulnerabilitiesRequest
      >,
    ) {
      state.mostWidelyDeployedLibraries = QueryState.next(
        state.mostWidelyDeployedLibraries,
        action,
      );
    },

    getVulnerableEntitiesWithSensitiveDataAccess(
      state,
      action: QueryAction<
        CountSummary,
        ResourceSummaryApiGetSummaryByTypeRequest
      >,
    ) {
      state.vulnerableEntitiesWithSensitiveDataAccess = QueryState.next(
        state.vulnerableEntitiesWithSensitiveDataAccess,
        action,
      );
    },

    getCriticalAndHighVulnerabilitiesSummary(
      state,
      action: QueryAction<
        VulnerabilityDashboardCriticalHigh,
        DefaultApiGetCriticalAndHighSeveritySummaryRequest
      >,
    ) {
      state.criticalAndHighVulnerabilitiesSummary = QueryState.next(
        state.criticalAndHighVulnerabilitiesSummary,
        action,
      );
    },

    getEOLSoftware(
      state,
      action: QueryAction<
        EOLDashboard,
        VulnerabilitiesApiGetEolDashboardDetailsRequest
      >,
    ) {
      state.eolSoftware = QueryState.next(state.eolSoftware, action);
    },
    getAllEOLSoftware(
      state,
      action: QueryAction<
        EOLDashboard,
        VulnerabilitiesApiGetEolDashboardDetailsRequest
      >,
    ) {
      state.allEolSoftware = PagedQueryState.next(
        state.allEolSoftware,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r?.total_count,
        },
      );
    },
    updateEOLSoftwarePagination(state, action: PayloadAction<PageInfo>) {
      state.allEolSoftware.page.info = action.payload;
    },

    getEOLSoftwareEntities(
      state,
      action: QueryAction<
        InsightV2Pagination,
        VulnerabilitiesApiGetEolEntitiesDashboardDetailsRequest
      >,
    ) {
      state.eolSoftwareEntities = PagedQueryState.next(
        state.eolSoftwareEntities,
        action,
        {
          mapData: r => r.items,
          mapTotalCount: r => r?.total || 0,
        },
      );
    },
    updateEOLSoftwareEntitiesPagination(
      state,
      action: PayloadAction<PageInfo>,
    ) {
      state.eolSoftwareEntities.page.info = action.payload;
    },

    /*get risk context*/
    getRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.riskContext = QueryState.next(state.riskContext, action);
    },

    getVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.totalVulnerabilities = QueryState.next(
        state.vulnerabilitiesCount.totalVulnerabilities,
        action,
      );
    },
    getVmVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.vm = QueryState.next(
        state.vulnerabilitiesCount.vm,
        action,
      );
    },
    getContainerVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.container = QueryState.next(
        state.vulnerabilitiesCount.container,
        action,
      );
    },
    getContainerServiceVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.containerService = QueryState.next(
        state.vulnerabilitiesCount.containerService,
        action,
      );
    },
    getVmImageVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.vmImage = QueryState.next(
        state.vulnerabilitiesCount.vmImage,
        action,
      );
    },
    getServerlessVulnerabilitiesCount(
      state,
      action: QueryAction<
        VulnerabilityDashboardCount,
        DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
      >,
    ) {
      state.vulnerabilitiesCount.serverless = QueryState.next(
        state.vulnerabilitiesCount.serverless,
        action,
      );
    },

    getVulnerabilityPrioritizationData(
      state,
      action: QueryAction<
        VulnerabilityPrioritization,
        DefaultApiGetVulnerabilityPriortizationDataRequest
      >,
    ) {
      state.vulnerabilityPrioritization = QueryState.next(
        state.vulnerabilityPrioritization,
        action,
      );
    },

    getVulnerabilitiesByResourceTypes(
      state,
      action: QueryAction<
        VulnerabilityDashboardBySources,
        DefaultApiGetBySourcesDashboardDataRequest
      >,
    ) {
      state.vulnerabilitiesByResourceTypes = QueryState.next(
        state.vulnerabilitiesByResourceTypes,
        action,
      );
    },

    getExploitableEntitiesToPatch(
      state,
      action: QueryAction<any, ChartsApiGetVulnerabilitiesDetailsRequest>,
    ) {
      state.exploitableEntitiesToPatch = QueryState.next(
        state.exploitableEntitiesToPatch,
        action,
      );
    },
  },
});

export const { actions, reducer, name: sliceKey } = vulnerabilityOverviewSlice;
